import { type Editor, Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'

export interface NodeConfigState {
  // The node whose configuration sheet is open, followed through edits;
  // null when none is, or the node was deleted.
  pos: number | null
}

export const NODE_CONFIG_KEY = new PluginKey<NodeConfigState>('nodeConfig')

export interface NodeConfigStorage {
  // Sheet hosts mounted beside the editor; none, and opening a node's
  // configuration shows nothing.
  hosts: number
}

declare module '@tiptap/core' {
  interface Storage {
    nodeConfig?: NodeConfigStorage
  }

  interface Commands<ReturnType> {
    nodeConfig: {
      openNodeConfig: (pos: number) => ReturnType
      closeNodeConfig: () => ReturnType
    }
  }
}

// Which observation node or conditional section the configuration sheet
// beside the editor is open on.
export const NodeConfigExtension = Extension.create({
  name: 'nodeConfig',

  addStorage(): NodeConfigStorage {
    return { hosts: 0 }
  },

  addCommands() {
    return {
      openNodeConfig:
        (pos) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(NODE_CONFIG_KEY, { pos }).setMeta('addToHistory', false)
          }
          return true
        },
      closeNodeConfig:
        () =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(NODE_CONFIG_KEY, { pos: null }).setMeta(
              'addToHistory',
              false
            )
          }
          return true
        },
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<NodeConfigState>({
        key: NODE_CONFIG_KEY,
        state: {
          init: () => ({ pos: null }),
          apply: (tr, previous) => {
            const meta = tr.getMeta(NODE_CONFIG_KEY) as
              | NodeConfigState
              | undefined
            if (meta) {
              return meta
            }
            if (previous.pos === null || !tr.docChanged) {
              return previous
            }
            const mapped = tr.mapping.mapResult(previous.pos, 1)
            return { pos: mapped.deleted ? null : mapped.pos }
          },
        },
      }),
    ]
  },
})

// Whether `openNodeConfig` shows a sheet: a host is mounted beside the editor.
export function canConfigureNodes(editor: Editor): boolean {
  return (editor.storage.nodeConfig?.hosts ?? 0) > 0
}
